/**
 * Assembly & trim (Montaj & Trim / Assembly & Trim / Montage & Trim): page copy + SEO.
 * Every string is VERBATIM from the pre-redesign MontajTrim.jsx,
 * en/AssemblyTrim.jsx and de/MontageTrim.jsx. The `seo` blocks reproduce the
 * exact head writes each page made (TR: keywords, OG, Twitter, Service JSON-LD;
 * EN and DE: title and description only; the canonical is CanonicalManager's
 * now). Key order inside `schema` is load-bearing (byte-identical
 * JSON.stringify).
 */

const assembly = {
  tr: {
    seo: {
      title: 'Montaj & Trim Parçaları Hizmetleri | Otomotiv Parça Montajı - Ankaplastik Bursa',
      description:
        "Otomotiv trim parçaları montajı ve parça montajı hizmetleri. ISOFIX güvenlik bileşenleri, klips montajı, dekoratif trim parça birleştirme. Bursa'da ISO 9001 sertifikalı üretim tesisi.",
      keywords:
        'trim parçaları, parça montajı, otomotiv montaj, ISOFIX montaj, klips montajı, trim assembly, otomotiv iç parça, Bursa parça montajı',
      og: {
        'og:title': 'Montaj & Trim Parçaları | Ankaplastik Bursa',
        'og:description':
          'Otomotiv iç trim parçaları ve güvenlik bileşenlerinin hassas montajı. ISOFIX, klips ve dekoratif trim uzmanı.',
        'og:type': 'website',
        'og:url': 'https://ankaplastik.com/montaj-trim',
        'og:image': 'https://ankaplastik.com/og-montaj-trim.jpg',
        'og:image:width': '1200',
        'og:image:height': '630',
        'og:locale': 'tr_TR',
        'og:site_name': 'Ankaplastik',
      },
      twitter: {
        'twitter:card': 'summary_large_image',
        'twitter:title': 'Montaj & Trim Parçaları | Ankaplastik',
        'twitter:description': 'Otomotiv trim parçaları ve güvenlik bileşenleri montajı. ISOFIX uzmanı.',
        'twitter:image': 'https://ankaplastik.com/og-montaj-trim.jpg',
      },
      schemaId: 'montaj-trim',
      schema: {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'Montaj & Trim Parçaları Hizmetleri',
        alternateName: ['Trim Assembly', 'Parça Montajı'],
        provider: {
          '@type': 'LocalBusiness',
          '@id': 'https://ankaplastik.com/#organization',
          name: 'Ankaplastik',
        },
        serviceType: 'Otomotiv Parça Montajı',
        areaServed: {
          '@type': 'Country',
          name: 'Türkiye',
        },
        description:
          'Otomotiv iç trim parçaları ve güvenlik bileşenlerinin hassas montajı. ISOFIX güvenlik parçaları, klips ve bağlantı elemanları, dekoratif trim montajı.',
        offers: {
          '@type': 'Offer',
          availability: 'https://schema.org/InStock',
          priceSpecification: {
            '@type': 'PriceSpecification',
            priceCurrency: 'TRY',
          },
        },
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'Montaj Hizmetleri',
          itemListElement: [
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'ISOFIX Güvenlik Bileşenleri Montajı' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Klips ve Bağlantı Elemanları Montajı' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Gösterge Paneli Aksam Montajı' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Kapı Paneli Bileşenleri Montajı' } },
          ],
        },
      },
    },
    hero: {
      titleLead: 'Montaj & Trim',
      titleTail: 'Hizmetleri',
      lead: 'Otomotiv iç trim parçaları ve güvenlik bileşenlerinin hassas montajı için deneyimli ekibimiz ve kontrollü proseslerimizle hizmetinizdeyiz.',
    },
    intro: {
      title: 'Hassas Parça Birleştirme',
      paragraphs: [
        'Otomotiv trim parçaları montajı, araç iç mekanındaki plastik, metal ve kompozit bileşenlerin hassas bir şekilde birleştirilmesi işlemidir.',
        'Klipsler, ISOFIX güvenlik parçaları ve dekoratif trim elemanlarının montajında uzmanlaşmış ekibimiz, sıkı kalite standartlarına uygun üretim gerçekleştirmektedir.',
      ],
    },
    advantages: [
      { title: 'Hassas Montaj', desc: 'Mikron toleranslarla hassas parça birleştirme' },
      { title: 'Kalite Kontrol', desc: 'Her aşamada sıkı kalite denetimi' },
      { title: 'Hızlı Teslimat', desc: 'Seri üretimde kısa teslim süreleri' },
      { title: 'Güvenlik Parçaları', desc: 'ISOFIX ve güvenlik bileşenleri uzmanlığı' },
      { title: 'Çeşitli Parçalar', desc: 'Klips, bağlantı elemanı ve trim parçaları' },
      { title: 'İzlenebilirlik', desc: 'Tam üretim ve parti takibi' },
    ],
    applications: [
      'Araç içi trim parçaları montajı',
      'ISOFIX güvenlik bileşenleri',
      'Klips ve bağlantı elemanları',
      'Gösterge paneli aksam montajı',
      'Kapı paneli bileşenleri',
      'Konsol ve aksesuar montajı',
    ],
    stats: [
      { label: 'Deneyim', value: '25+ yıl' },
      { label: 'Hata Oranı', value: '%0.01' },
    ],
    contact: {
      description: 'Montaj ve trim ihtiyaçlarınız için bizimle iletişime geçin.',
      subjectPrefix: 'Montaj & Trim',
    },
    related: [
      {
        service: 'pad',
        name: 'Tampon Baskı',
        desc: '3D yüzeylere mükemmel uyum sağlayan pad printing teknolojisi sunuyoruz.',
        features: ['Mikron hassasiyetinde baskı', 'Çok renkli uygulamalar'],
      },
      {
        service: 'screen',
        name: 'Serigrafi Baskı',
        desc: 'Geniş yüzeylerde yüksek kaliteli elek baskı (screen printing) hizmetleri sunuyoruz.',
        features: ['Büyük alan baskı kapasitesi', 'Yüksek mürekkep kalınlığı'],
      },
    ],
  },

  en: {
    seo: {
      title: 'Assembly & Trim Parts | Automotive Component Assembly - Ankaplastik Turkey',
      description:
        'Precision assembly services for automotive trim parts and safety components. ISOFIX, clips, decorative trim. 100% functional testing, ISO 9001:2015 certified.',
    },
    hero: {
      titleLead: 'Assembly &',
      titleTail: 'Trim Parts',
      lead: 'Precision assembly of multi-component automotive parts and decorative trim applications. 100% quality tested with full traceability.',
    },
    intro: {
      title: 'Component Integration Services',
      paragraphs: [
        'Our assembly services bring together multiple components into finished sub-assemblies ready for OEM integration. From simple clip insertion to complex multi-part assemblies.',
        'We specialize in automotive interior trim parts, safety components like ISOFIX anchors, and decorative applications with printed surfaces.',
      ],
    },
    advantages: [
      { title: 'Quality Assured', desc: '100% functional testing on every assembled component' },
      { title: 'Just-in-Time Delivery', desc: 'Reliable delivery schedules meeting OEM requirements' },
      { title: 'Multi-Component Assembly', desc: 'Complex multi-part integration with precision' },
      { title: 'Diverse Capabilities', desc: 'Assembly of clips, inserts, and decorative elements' },
      { title: 'High Efficiency', desc: 'Streamlined processes for cost-effective production' },
      { title: 'Traceability', desc: 'Full component traceability for quality management' },
    ],
    applications: [
      'ISOFIX child seat anchors',
      'Interior trim panel assembly',
      'Dashboard component integration',
      'Safety clip installation',
      'Decorative trim applications',
      'Sub-assembly packaging',
    ],
    stats: [
      { label: 'Quality Tested', value: '100%' },
      { label: 'Defect Rate', value: '0.01%' },
    ],
    contact: {
      description: 'Contact us for your assembly and trim requirements.',
      subjectPrefix: 'Assembly & Trim',
    },
    related: [
      {
        service: 'pad',
        name: 'Pad Printing',
        desc: 'Precision pad printing for complex 3D surfaces and irregular geometries.',
        features: ['Multi-color printing', 'Micron-level precision'],
      },
      {
        service: 'screen',
        name: 'Screen Printing',
        desc: 'High-quality screen printing for large surfaces and control panels.',
        features: ['Large area coverage', 'Superior ink deposit'],
      },
    ],
  },

  de: {
    seo: {
      title: 'Montage & Trimteile | Automobilkomponentenmontage - Ankaplastik Türkei',
      description:
        'Präzisionsmontage von Fahrzeug-Trimteilen und Sicherheitskomponenten. ISOFIX, Clips, Zierleisten. 100% Funktionsprüfung, ISO 9001:2015 zertifiziert.',
    },
    hero: {
      titleLead: 'Montage &',
      titleTail: 'Trimteile',
      lead: 'Präzisionsmontage von Mehrkomponenten-Fahrzeugteilen und dekorativen Trim-Anwendungen. 100% qualitätsgeprüft mit voller Rückverfolgbarkeit.',
    },
    intro: {
      title: 'Komponenten-Integrationsservices',
      paragraphs: [
        'Unsere Montageservices bringen mehrere Komponenten zu fertigen Baugruppen zusammen, die für die OEM-Integration bereit sind. Von der einfachen Clip-Montage bis hin zu komplexen Mehrteil-Baugruppen.',
        'Wir sind spezialisiert auf Fahrzeuginnenraum-Trimteile, Sicherheitskomponenten wie ISOFIX-Verankerungen und dekorative Anwendungen mit bedruckten Oberflächen.',
      ],
    },
    advantages: [
      { title: 'Qualitätssicherung', desc: '100% Funktionsprüfung bei jeder montierten Komponente' },
      { title: 'Just-in-Time-Lieferung', desc: 'Zuverlässige Lieferpläne, die OEM-Anforderungen erfüllen' },
      { title: 'Mehrkomponenten-Montage', desc: 'Komplexe Teileintegration mit Präzision' },
      { title: 'Vielseitige Fähigkeiten', desc: 'Montage von Clips, Einsätzen und dekorativen Elementen' },
      { title: 'Hohe Effizienz', desc: 'Optimierte Prozesse für kostengünstige Produktion' },
      { title: 'Rückverfolgbarkeit', desc: 'Volle Komponentenrückverfolgbarkeit für das Qualitätsmanagement' },
    ],
    applications: [
      'ISOFIX-Kindersitzverankerungen',
      'Montage von Innenverkleidungsteilen',
      'Integration von Armaturenbrettkomponenten',
      'Installation von Sicherheitsclips',
      'Dekorative Trim-Anwendungen',
      'Verpackung von Baugruppen',
    ],
    stats: [
      { label: 'Qualitätsgeprüft', value: '100%' },
      { label: 'Fehlerquote', value: '0.01%' },
    ],
    contact: {
      description: 'Kontaktieren Sie uns für Ihre Montage- und Trim-Anforderungen.',
      subjectPrefix: 'Montage & Trim',
    },
    related: [
      {
        service: 'pad',
        name: 'Tampondruck',
        desc: 'Präzisions-Tampondruck für komplexe 3D-Oberflächen.',
        features: ['Mehrfarbendruck', 'Mikrometergenaue Präzision'],
      },
      {
        service: 'screen',
        name: 'Siebdruck',
        desc: 'Hochwertiger Siebdruck für große Oberflächen.',
        features: ['Großflächenabdeckung', 'Überlegener Farbauftrag'],
      },
    ],
  },
}

export default assembly
